import {
  ErrorModal,
  dxtToLocalServerTime,
  getMomentTz,
  HelmetDexteel,
} from "@dexteel/mesf-core";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { Grid, IconButton, Paper, Typography } from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import {
  ColDef,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  RowClassParams,
  RowStyle,
  ValueGetterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useState } from "react";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";
import { SampleFilterSelector } from "@/shared/selectors/SampleFilterSelector";
import { GradeFilterSelector } from "@/shared/selectors/GradeFilterSelector";
import { LinkCellRenderer } from "@/controls/ag-grid/components/ButtonCellRenderer";
import { EditSampleModal } from "./components/EditSampleModal";
import { useChemistriesReportQuery } from "./hooks/queries/useChemistriesReportQuery";

const moment = getMomentTz();

const FIXED_FIELDS = [
  "Date",
  "Shift",
  "Crew",
  "Heat",
  "Grade",
  "Type",
  "Reception",
  "Reported",
  "Technician",
] as const;

export const ChemistriesReportPage = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSample, setSelectedSample] = useState<{
    ChemistrySampleId: number;
    HeatId: number;
    Heat: string;
    Grade: string;
  } | null>(null);

  const [startDate, setStartDate] = useState(() => moment().subtract(1, "day"));
  const [endDate, setEndDate] = useState(() => moment());
  const [sampleTypeId, setSampleTypeId] = useState(0);
  const [gradeId, setGradeId] = useState(0);

  const { data, isLoading, error } = useChemistriesReportQuery(
    startDate.clone().startOf("day").utc().toDate(),
    endDate.clone().endOf("day").utc().toDate(),
    sampleTypeId,
    gradeId,
  );

  const pivotedRows = data?.rows ?? [];
  const elementNames = data?.elementNames ?? [];

  const getRowStyle = (params: RowClassParams): RowStyle => {
    if (params.data?.IsDuplicate) {
      return { backgroundColor: "#fff3cd" };
    }
    return {};
  };

  const columns: ColDef[] = useMemo(() => {
    const editCol: ColDef = {
      headerName: "",
      width: 50,
      flex: 0,
      pinned: "left" as const,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data) return null;
        return (
          <IconButton
            color="primary"
            size="small"
            onClick={() => {
              params.node.setSelected(true);
              setSelectedSample({
                ChemistrySampleId: params.data.ChemistrySampleId as number,
                HeatId: params.data.HeatId as number,
                Heat: params.data.Heat as string,
                Grade: params.data.Grade as string,
              });
              setEditModalOpen(true);
            }}
            title="Edit sample"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        );
      },
    };

    const fixedCols: ColDef[] = FIXED_FIELDS.map((field) => {
      const colDef: ColDef = {
        field,
        headerName: field,
        width:
          field === "Date"
            ? 100
            : field === "Technician"
              ? 95
              : field === "Grade"
                ? 100
                : field === "Reception" || field === "Reported"
                  ? 80
                  : 65,
        flex: 0,
        pinned: "left" as const,
        sortable: true,
      };

      if (field === "Heat") {
        colDef.cellRenderer = (params: ICellRendererParams) => {
          if (!params.data?.EAFProcessId) return params.value;
          return LinkCellRenderer({
            ...params,
            link: `/lab-data-entry/${params.data.EAFProcessId}`,
          });
        };
      }

      if (field === "Reception" || field === "Reported") {
        colDef.valueGetter = (params: ValueGetterParams) => {
          if (params.data?.[field]) {
            return dxtToLocalServerTime(params.data[field], "HH:mm");
          }
          return "";
        };
      }

      return colDef;
    });

    const elementCols: ColDef[] = elementNames.map((name) => ({
      field: name,
      headerName: name === "FEPercentage" ? "FE%" : name,
      flex: 1,
      minWidth: 60,
      sortable: true,
    }));

    return [...fixedCols, editCol, ...elementCols];
  }, [elementNames]);

  const handleExportToExcel = useCallback(() => {
    if (!gridApi) {
      setNotificationMessage("Please wait until the table is loaded");
      return;
    }
    gridApi.exportDataAsExcel({
      fileName: `Chemistries_${startDate.format("YYYY-MM-DD")}_to_${endDate.format("YYYY-MM-DD")}.xlsx`,
      sheetName: "Chemistry Samples",
    });
    setNotificationMessage("Export to Excel completed successfully");
  }, [gridApi, startDate, endDate]);

  const getRowId = useCallback(
    (params: GetRowIdParams) => String(params.data.ChemistrySampleId),
    [],
  );

  const handleGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
  };

  return (
    <>
      <HelmetDexteel title="Chemistry Samples" />
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Chemistry Samples
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }} elevation={2}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid
                item
                container
                xs={12}
                md={8}
                spacing={2}
                alignItems="center"
              >
                <Grid item xs={6} sm={3} md={2}>
                  <DatePicker
                    label="Start"
                    value={startDate}
                    onChange={(val) => val && setStartDate(val)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                        size: "small",
                        InputLabelProps: { shrink: true },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <DatePicker
                    label="End"
                    value={endDate}
                    onChange={(val) => val && setEndDate(val)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                        size: "small",
                        InputLabelProps: { shrink: true },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <SampleFilterSelector
                    value={sampleTypeId}
                    onChange={setSampleTypeId}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <GradeFilterSelector value={gradeId} onChange={setGradeId} />
                </Grid>
                <Grid item>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        backgroundColor: "#fff3cd",
                        marginRight: 4,
                      }}
                    />
                    <span style={{ fontSize: "0.7rem" }}>Duplicate</span>
                  </div>
                </Grid>
              </Grid>
              <Grid item xs={12} md={2} container justifyContent="flex-end">
                <ExcelButton
                  handleExportToExcel={handleExportToExcel}
                  disabled={!gridApi || isLoading || pivotedRows.length === 0}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper
            elevation={2}
            sx={{ height: "calc(100vh - 260px)", width: "100%" }}
          >
            <AgGridReact
              rowData={pivotedRows}
              columnDefs={columns}
              defaultColDef={defaultColDef}
              loading={isLoading}
              getRowId={getRowId}
              getRowStyle={getRowStyle}
              onGridReady={handleGridReady}
              {...defaultGridOptions}
            />
          </Paper>
        </Grid>
      </Grid>
      <EditSampleModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSaved={() => {
          setNotificationMessage("Sample updated successfully");
        }}
        sample={selectedSample}
      />
      <ErrorModal
        error={errorMessage || (error instanceof Error ? error.message : "")}
        onHide={() => setErrorMessage("")}
      />
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
    </>
  );
};
